import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Shield } from 'lucide-react';
import { StatusBadge } from '../../components/UI/StatusBadge';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import FormSelect from '../../components/UI/FormSelect';
import DatePickerField from '../../components/UI/DatePickerField';
import ConfirmModal from '../../components/UI/ConfirmModal';
import ProcessProgressRail from '../../components/UI/ProcessProgressRail';
import EventTimeline from '../../components/UI/EventTimeline';
import { useIsMobile } from '../../hooks/useIsMobile';
import {
  useHRWorkPassDecision,
  useHRWorkPassDecisions,
  useHRWorkPassRenewals,
  useResolveWorkPassException,
  useUpdateWorkPassRenewal,
  useWorkPassCapabilities,
} from '../../hooks/useWorkPassDecisions';
import WorkPassNotificationsPausedBanner from '../../components/workPass/WorkPassNotificationsPausedBanner';
import { formatDate, parseLocalDate, toLocalISODate } from '../../utils/dateUtils';

const EVENT_LABELS = {
  created: 'Cycle opened',
  recommended: 'Recommendation recorded',
  approved: 'Department approved',
  returned: 'Returned for a new recommendation',
  configuration_exception: 'Configuration exception',
  exception_resolved: 'Exception resolved',
  renewal_created: 'Renewal work created',
  renewal_updated: 'Renewal updated',
  renewed: 'Pass renewed',
  returned_from_renewal: 'Returned from renewal',
  terminated_promoted: 'Termination promoted',
  superseded: 'Superseded',
  locked_for_reconciliation: 'Locked for HR reconciliation',
};

const RENEWAL_STATUSES = [
  { value: 'pending_hr_action', label: 'Pending HR action' },
  { value: 'in_preparation', label: 'In preparation' },
  { value: 'submitted_to_mom', label: 'Submitted to MOM' },
  { value: 'renewed', label: 'Renewed' },
  { value: 'blocked', label: 'Blocked' },
  { value: 'failed', label: 'Failed' },
];

const EXCEPTION_ACTIONS = [
  { value: 'return_to_recommendation', label: 'Return to recommendation' },
  { value: 'advance_to_approval', label: 'Advance to approval' },
  { value: 'cancel', label: 'Cancel cycle' },
];

const WorkPassBoard = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const board = searchParams.get('board') === 'renewals' ? 'renewals' : 'decisions';
  const selectedId = searchParams.get('id') || '';
  const staffId = searchParams.get('staff_id') || '';
  const isCompact = useIsMobile(1024);
  const [renewalForm, setRenewalForm] = useState({
    status: '',
    owner_staff_id: '',
    mom_reference: '',
    evidence_note: '',
    blocker_reason: '',
    new_expiry: null,
  });
  const [exception, setException] = useState({ action: '', reason: '' });
  const [confirm, setConfirm] = useState(null);

  const decisionParams = useMemo(() => (staffId ? { staff_id: staffId } : {}), [staffId]);
  const { data: caps } = useWorkPassCapabilities();
  const { data: decisions = [], isLoading: decisionsLoading } = useHRWorkPassDecisions(decisionParams);
  const { data: renewals = [], isLoading: renewalsLoading } = useHRWorkPassRenewals();
  const { data: detail, isLoading: detailLoading } = useHRWorkPassDecision(selectedId);
  const updateRenewal = useUpdateWorkPassRenewal();
  const resolveException = useResolveWorkPassException();

  const list = board === 'renewals' ? renewals : decisions;
  const loading = board === 'renewals' ? renewalsLoading : decisionsLoading;
  const listPaneRef = useRef(null);
  const detailPaneRef = useRef(null);

  useEffect(() => {
    if (isCompact || !selectedId || loading) return undefined;
    const frame = window.requestAnimationFrame(() => {
      const pane = listPaneRef.current;
      const card = pane?.querySelector('[data-selected="true"]');
      if (!pane || !card) return;
      const paneRect = pane.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();
      if (cardRect.top < paneRect.top) {
        pane.scrollTop += cardRect.top - paneRect.top;
      } else if (cardRect.bottom > paneRect.bottom) {
        pane.scrollTop += cardRect.bottom - paneRect.bottom;
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [isCompact, selectedId, loading]);

  useEffect(() => {
    if (isCompact || !selectedId) return;
    detailPaneRef.current?.scrollTo({ top: 0 });
  }, [isCompact, selectedId]);

  const openDetail = (id) => {
    const next = new URLSearchParams(searchParams);
    next.set('id', id);
    setSearchParams(next);
  };

  const changeBoard = (nextBoard) => {
    const next = new URLSearchParams(searchParams);
    next.set('board', nextBoard);
    next.delete('id');
    setSearchParams(next);
  };

  const closeCompact = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('id');
    setSearchParams(next);
  };

  const saveRenewal = () => {
    updateRenewal.mutate({
      id: selectedId,
      data: {
        status: renewalForm.status,
        owner_staff_id: renewalForm.owner_staff_id,
        mom_reference: renewalForm.mom_reference,
        evidence_note: renewalForm.evidence_note,
        blocker_reason: renewalForm.blocker_reason,
        new_expiry: toLocalISODate(renewalForm.new_expiry) || null,
      },
    });
  };

  return (
    <div className="flex flex-col gap-4 lg:flex-1 lg:min-h-0 lg:overflow-hidden">
      <div className="flex items-center gap-2 flex-shrink-0">
        {[
          { id: 'decisions', label: 'Decisions' },
          { id: 'renewals', label: 'Renewals' },
        ].map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => changeBoard(item.id)}
            className={`tab-btn-admin ${board === item.id ? 'active' : ''}`}
          >
            {item.label}
          </button>
        ))}
      </div>

      <WorkPassNotificationsPausedBanner paused={!!caps?.notifications_paused} />

      {staffId && (
        <p className="text-sm text-gray-600 flex-shrink-0">
          Filtered to staff <span className="font-medium tabular-nums">{staffId}</span>
        </p>
      )}

      {loading ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-8 flex justify-center lg:flex-1">
          <LoadingSpinner size="md" />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-4 lg:gap-6 lg:flex-1 lg:min-h-0 lg:overflow-hidden">
          <div
            ref={listPaneRef}
            className={`${
              isCompact && selectedId ? 'hidden' : 'space-y-3'
            } lg:min-h-0 lg:overflow-y-auto lg:overscroll-y-contain lg:pr-1`}
          >
            {list.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center">
                <Shield className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-600 font-medium">No work-pass records</p>
              </div>
            ) : (
              list.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  data-selected={selectedId === item.id ? 'true' : undefined}
                  onClick={() => openDetail(item.id)}
                  className={`w-full text-left bg-white rounded-2xl border p-4 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 ${
                    selectedId === item.id ? 'border-red-600 shadow-md' : 'border-gray-200 hover:shadow-md'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{item.worker_name_snapshot}</p>
                      <p className="text-xs text-gray-500 mt-0.5 truncate">
                        {item.request_number} · {item.worker_staff_id}
                      </p>
                    </div>
                    <StatusBadge status={item.renewal?.status || item.status} />
                  </div>
                  <p className="text-xs text-gray-500 mt-2 tabular-nums">
                    Expires {formatDate(item.expiry_date)}
                    {item.is_decision_overdue || item.is_execution_overdue ? ' · overdue' : ''}
                  </p>
                </button>
              ))
            )}
          </div>

          <div
            ref={detailPaneRef}
            className={`${
              isCompact && !selectedId ? 'hidden' : ''
            } lg:min-h-0 lg:h-full lg:overflow-y-auto lg:overscroll-y-contain lg:pr-1`}
          >
            {isCompact && selectedId && (
              <button type="button" onClick={closeCompact} className="btn btn-admin-outline mb-3">
                <ArrowLeft className="w-4 h-4" />
                Back to list
              </button>
            )}
            {!selectedId ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center text-sm text-gray-500 lg:h-full lg:flex lg:flex-col lg:items-center lg:justify-center">
                Select a cycle to review or execute it
              </div>
            ) : detailLoading || !detail ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-8 flex justify-center lg:h-full lg:items-center">
                <LoadingSpinner size="md" />
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">{detail.worker_name_snapshot}</h2>
                    <p className="text-sm text-gray-500">{detail.request_number} · {detail.worker_staff_id}</p>
                  </div>
                  <StatusBadge status={detail.status} />
                </div>
                {(detail.is_decision_overdue || detail.is_execution_overdue || detail.locked_for_reconciliation) && (
                  <p className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-xl p-3 flex gap-2">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    {detail.locked_for_reconciliation
                      ? (detail.lock_reason || 'Locked for HR reconciliation. A submitted MOM renewal is not withdrawn automatically.')
                      : 'This cycle is overdue against the 60-day or 30-day threshold.'}
                  </p>
                )}
                {detail.config_exception_reason && (
                  <p className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-xl p-3">
                    {detail.config_exception_reason}
                  </p>
                )}
                <ProcessProgressRail
                  theme="admin"
                  steps={detail.progress || []}
                  pendingWith={detail.progress?.find((step) => step.state === 'current')?.subtitle || ''}
                />
                <dl className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <dt className="text-xs text-gray-500">Pass</dt>
                    <dd className="font-medium text-gray-900">{detail.pass_type_label || detail.pass_type || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-gray-500">Expiry</dt>
                    <dd className="font-medium text-gray-900 tabular-nums">{formatDate(detail.expiry_date)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-gray-500">Recommended</dt>
                    <dd className="font-medium text-gray-900">{detail.recommended_outcome?.replace(/_/g, ' ') || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-gray-500">Department</dt>
                    <dd className="font-medium text-gray-900">{detail.department_name || '—'}</dd>
                  </div>
                </dl>

                {detail.status === 'configuration_exception' && (
                  <div className="space-y-3 border-t border-gray-100 pt-4">
                    <FormSelect
                      theme="admin"
                      options={EXCEPTION_ACTIONS}
                      value={exception.action}
                      onChange={(value) => setException((current) => ({ ...current, action: value }))}
                      placeholder="Resolve exception"
                    />
                    <textarea
                      className="input-admin w-full"
                      rows={2}
                      placeholder="Reason"
                      value={exception.reason}
                      onChange={(event) => setException((current) => ({ ...current, reason: event.target.value }))}
                    />
                    <button
                      type="button"
                      className="btn btn-admin-primary w-full"
                      disabled={!exception.action || !exception.reason.trim() || resolveException.isPending}
                      onClick={() => setConfirm({ kind: 'exception' })}
                    >
                      Resolve exception
                    </button>
                  </div>
                )}

                {detail.renewal && (
                  <div className="space-y-3 border-t border-gray-100 pt-4">
                    <h3 className="text-sm font-semibold text-gray-900">Renewal {detail.renewal.request_number}</h3>
                    <StatusBadge status={detail.renewal.status} />
                    <FormSelect
                      theme="admin"
                      options={RENEWAL_STATUSES}
                      value={renewalForm.status || detail.renewal.status}
                      onChange={(value) => setRenewalForm((current) => ({ ...current, status: value }))}
                      placeholder="Renewal status"
                    />
                    <input
                      className="input-admin w-full"
                      placeholder="Owner staff ID"
                      value={renewalForm.owner_staff_id}
                      onChange={(event) => setRenewalForm((current) => ({ ...current, owner_staff_id: event.target.value }))}
                    />
                    <input
                      className="input-admin w-full"
                      placeholder="MOM reference"
                      value={renewalForm.mom_reference}
                      onChange={(event) => setRenewalForm((current) => ({ ...current, mom_reference: event.target.value }))}
                    />
                    <textarea
                      className="input-admin w-full"
                      rows={2}
                      placeholder="Evidence note"
                      value={renewalForm.evidence_note}
                      onChange={(event) => setRenewalForm((current) => ({ ...current, evidence_note: event.target.value }))}
                    />
                    <textarea
                      className="input-admin w-full"
                      rows={2}
                      placeholder="Blocker / failed reason"
                      value={renewalForm.blocker_reason}
                      onChange={(event) => setRenewalForm((current) => ({ ...current, blocker_reason: event.target.value }))}
                    />
                    <div>
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">New expiry</span>
                      <DatePickerField
                        selected={renewalForm.new_expiry || parseLocalDate(detail.renewal.new_expiry)}
                        onChange={(value) => setRenewalForm((current) => ({ ...current, new_expiry: value }))}
                        theme="admin"
                      />
                    </div>
                    <p className="text-xs text-gray-500">
                      Completing a renewal writes the new expiry to the worker profile and keeps {formatDate(detail.renewal.previous_expiry)} on this snapshot.
                    </p>
                    <button
                      type="button"
                      className="btn btn-admin-primary w-full"
                      disabled={updateRenewal.isPending}
                      onClick={() => setConfirm({ kind: 'renewal' })}
                    >
                      Update renewal
                    </button>
                  </div>
                )}

                <EventTimeline
                  events={(detail.events || []).map((event) => ({
                    id: event.id,
                    label: EVENT_LABELS[event.event_type] || event.event_type,
                    subtitle: [event.actor_name, event.reason].filter(Boolean).join(' · '),
                    timestamp: event.created_at,
                    color: event.event_type === 'configuration_exception' ? 'red' : 'red',
                  }))}
                />
              </div>
            )}
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirm?.kind === 'renewal'}
        theme="admin"
        variant="primary"
        title="Update this renewal"
        confirmLabel="Save"
        isLoading={updateRenewal.isPending}
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          saveRenewal();
          setConfirm(null);
        }}
      >
        <p className="text-sm text-gray-700">
          Blocked or failed renewals return the cycle for a new departmental recommendation. Renewed writes the new expiry date.
        </p>
      </ConfirmModal>

      <ConfirmModal
        isOpen={confirm?.kind === 'exception'}
        theme="admin"
        variant="danger"
        title="Resolve configuration exception"
        confirmLabel="Resolve"
        isLoading={resolveException.isPending}
        onCancel={() => setConfirm(null)}
        onConfirm={() => resolveException.mutate(
          { id: selectedId, data: exception },
          { onSuccess: () => setConfirm(null) },
        )}
      >
        <p className="text-sm text-gray-700">This does not silently self-approve a retain or end decision.</p>
      </ConfirmModal>
    </div>
  );
};

export default WorkPassBoard;
